import type { BetterFeatureClientPlugin } from "@di-devs/better-feature/client";
import type { richmenu } from "../server/plugin";
import { createRichmenuCorePlugin } from "./richmenu/plugin";
import { createRichmenuTabGroupPlugin } from "./richmenu-tab-group/plugin";

/**
 * Rich Menu Client Plugin
 * Uses contracts to create atoms and signals for Better Feature client integration
 */
export const richmenuClient = () => {
	return {
		id: "richmenu",
		$InferServerPlugin: {} as ReturnType<typeof richmenu>,
		getAtoms: ($fetch) => {
			// Create subfeature plugins
			const richmenuCorePlugin = createRichmenuCorePlugin($fetch);
			const tabGroupPlugin = createRichmenuTabGroupPlugin($fetch);

			return {
				// Rich Menu core signals
				$getRichMenuTemplatesSignal:
					richmenuCorePlugin.signals.getRichMenuTemplates,
				$getRichMenuSignal: richmenuCorePlugin.signals.getRichMenu,
				$setRichMenuImageSignal: richmenuCorePlugin.signals.setRichMenuImage,

				// Rich Menu Tab Group signals
				$createRichMenuTabGroupSignal:
					tabGroupPlugin.signals.createRichMenuTabGroup,
				$getRichMenuTabGroupsSignal:
					tabGroupPlugin.signals.getRichMenuTabGroups,
				$getRichMenuTabGroupSignal: tabGroupPlugin.signals.getRichMenuTabGroup,
				$publishRichmenuTabGroupSignal:
					tabGroupPlugin.signals.publishRichmenuTabGroup,
				$unpublishRichmenuTabGroupSignal:
					tabGroupPlugin.signals.unpublishRichmenuTabGroup,
				$updateRichmenuTabGroupSignal:
					tabGroupPlugin.signals.updateRichmenuTabGroup,
				$deleteRichMenuTabGroupSignal:
					tabGroupPlugin.signals.deleteRichMenuTabGroup,

				// use hooks for atoms - Better Feature will add "use" prefix automatically

				// Rich Menu core queries - Better Feature will add "use" prefix automatically
				getRichMenuTemplates: richmenuCorePlugin.queries.getRichMenuTemplates,
				getRichMenu: richmenuCorePlugin.queries.getRichMenu,
				setRichMenuImage: richmenuCorePlugin.queries.setRichMenuImage,

				// Rich Menu Tab Group queries - Better Feature will add "use" prefix automatically
				createRichMenuTabGroup: tabGroupPlugin.queries.createRichMenuTabGroup,
				getRichMenuTabGroups: tabGroupPlugin.queries.getRichMenuTabGroups,
				getRichMenuTabGroup: tabGroupPlugin.queries.getRichMenuTabGroup,
				publishRichmenuTabGroup: tabGroupPlugin.queries.publishRichmenuTabGroup,
				unpublishRichmenuTabGroup:
					tabGroupPlugin.queries.unpublishRichmenuTabGroup,
				updateRichmenuTabGroup: tabGroupPlugin.queries.updateRichmenuTabGroup,
				deleteRichMenuTabGroup: tabGroupPlugin.queries.deleteRichMenuTabGroup,

				// State atoms from all plugins
				...richmenuCorePlugin.atoms,
				...tabGroupPlugin.atoms,
			};
		},
		getActions: ($fetch, $store) => {
			// Create subfeature plugins
			const richmenuCorePlugin = createRichmenuCorePlugin($fetch, $store);
			const tabGroupPlugin = createRichmenuTabGroupPlugin($fetch, $store);

			return {
				...richmenuCorePlugin.actions,
				...tabGroupPlugin.actions,
			};
		},
		atomListeners: [
			// Path-based invalidation patterns
			{
				signal: "$getRichMenuTabGroupsSignal",
				matcher: (path) => path.startsWith("/richmenu-tab-groups"),
			},
			{
				signal: "$getRichMenuTemplatesSignal",
				matcher: (path) => path.startsWith("/richmenus"),
			},
		],
	} satisfies BetterFeatureClientPlugin;
};
